import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  FaFolderOpen,
  FaLayerGroup,
  FaDesktop,
  FaRobot,
  FaPenFancy,
  FaGlobe,
  FaArrowRight,
  FaMagic,
} from 'react-icons/fa';
import ProductProofStrip from './ProductProofStrip';

const GROUPS = [
  {
    key: 'knowledge',
    Icon: FaFolderOpen,
    color: 'from-blue-400 to-blue-600',
    items: ['projects', 'grounded', 'brief'],
  },
  {
    key: 'experience',
    Icon: FaLayerGroup,
    color: 'from-indigo-400 to-indigo-600',
    items: ['genui', 'modes', 'labs'],
  },
  {
    key: 'presentations',
    Icon: FaDesktop,
    color: 'from-sky-400 to-sky-600',
    items: ['decks', 'visuals', 'editable'],
  },
  {
    key: 'intelligence',
    Icon: FaRobot,
    color: 'from-emerald-400 to-emerald-600',
    items: ['tutor', 'adaptive', 'languageLab'],
  },
  {
    key: 'creator',
    Icon: FaPenFancy,
    color: 'from-amber-400 to-amber-600',
    items: ['editor', 'facilitator', 'export'],
  },
  {
    key: 'reach',
    Icon: FaGlobe,
    color: 'from-rose-400 to-rose-600',
    items: ['languages', 'regions'],
  },
];

const SlideWhatsNew = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <main className="w-full bg-white dark:bg-slate-950">
      <section className="relative overflow-hidden py-12 md:py-16 px-4 sm:px-6 lg:px-8 border-y border-slate-200/80 dark:border-slate-800">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-blue-50/30 to-white dark:from-gray-950 dark:via-blue-950/10 dark:to-gray-950" />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/20 rounded-full border-2 border-blue-300 dark:border-blue-700 mb-4 shadow-sm"
            >
              <FaMagic className="text-blue-600 dark:text-blue-400 text-sm" aria-hidden />
              <span className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                {t('landing.slideWhatsNew.badge', "What's new")}
              </span>
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white mb-3 leading-tight tracking-tight">
              {t('landing.slideWhatsNew.title', 'Built for real learning, not another chatbot')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              {t(
                'landing.slideWhatsNew.subtitle',
                'New capabilities across knowledge, interactive lessons, AI presentations, tutoring, and multilingual delivery.',
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mb-10">
            {GROUPS.map((group, gi) => (
              <motion.div
                key={group.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: gi * 0.05 }}
                whileHover={{ y: -4 }}
                className="p-5 md:p-6 rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-xl transition-all"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${group.color} flex items-center justify-center text-white mb-4 shadow-md`}>
                  <group.Icon className="text-lg" aria-hidden />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-3">
                  {t(`landing.slideWhatsNew.groups.${group.key}.title`)}
                </h3>
                <ul className="space-y-2">
                  {group.items.map((item) => (
                    <li key={item} className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex gap-2">
                      <span className="text-blue-500 mt-0.5 shrink-0" aria-hidden>•</span>
                      <span>{t(`landing.slideWhatsNew.groups.${group.key}.items.${item}`)}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-14 md:mb-16">
            <button
              type="button"
              onClick={() => navigate('/features')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-sm shadow-lg shadow-blue-500/25 hover:from-blue-600 hover:to-blue-700 transition-all"
            >
              {t('landing.slideWhatsNew.cta', 'Explore all features')}
              <FaArrowRight className="text-xs" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => navigate('/platform')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 font-semibold text-sm hover:border-blue-400 dark:hover:border-blue-500 transition-all"
            >
              {t('landing.slideWhatsNew.ctaPlatform', 'Why Coursolo')}
            </button>
          </div>

          <ProductProofStrip />
        </div>
      </section>
    </main>
  );
};

export default SlideWhatsNew;
